/**
 * ADR-164 §6 S1/S2 — the Employment Information "contract" card.
 *
 * The card describes ONE contract: the primary if open, else the person's
 * latest (ADR-156). Its heading and dates come from `contractSummary`, which
 * the API builds from that same contract — never from the primary pointer,
 * which is cleared when a person leaves (that showed a leaver's last contract
 * as "Current contract", "Contract Start Date: —", "Open-ended").
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const fmt = (value) => dayjs.utc(value).format('DD/MM/YYYY');

/**
 * With several open contracts (concurrent contracts, CEE) "Current contract"
 * would read as the whole picture while the card shows only one of them. The
 * heading then names the contract and says how many are open, and the link
 * says it leads to all of them. With a single open contract — every PL&WE and
 * Americas person, most CEE people — the wording is unchanged: there is no
 * other contract to tell it apart from, so "primary" would only be jargon.
 * "Primary" is said only when the card's contract IS the recorded primary.
 *
 * @param {Object|null} summary - employee.contractSummary { episodeId, startDate, endDate, isClosed }
 * @param {Array|null} [contracts] - employee.contracts (ADR-156 §5: every OPEN
 *        contract, primary first; the latest alone when none is open)
 * @returns {{ heading: string, startText: string, endText: string, linkText: string }}
 */
export const contractCardText = (summary, contracts = null) => {
  const closed = Boolean(summary?.isClosed);
  const end = summary?.endDate ? fmt(summary.endDate) : null;
  const openCount = closed || !Array.isArray(contracts) ? 0 : contracts.length;
  const several = openCount > 1;
  const shown = several ? contracts.find((c) => c.id === summary?.episodeId) : null;

  let heading = 'Current contract';
  if (closed) {
    heading = `Last contract — ended ${end}`;
  } else if (several) {
    heading = `${shown?.isPrimary ? 'Primary contract' : 'Contract'} · 1 of ${openCount} open`;
  }

  return {
    heading,
    startText: `Contract Start Date: ${summary?.startDate ? fmt(summary.startDate) : '—'}`,
    endText: `Contract End Date: ${end || 'Open-ended'}`,
    linkText: several ? `View all ${openCount} on the Contracts tab` : 'View on the Contracts tab'
  };
};
